// sessionStorage key guarding against a reload loop when a fresh bundle still isn't served
// (e.g. CDN lag): we attempt the cache-busting reload at most once per target version per tab.
const RELOAD_GUARD_KEY = 'planfred_version_mismatch_reloaded'

// how many patch versions the running client may lag behind the latest before we force a reload;
// a gap of this many or fewer is left to chunk-reload.js (avoids churn during a patch rollout)
const PATCH_RELOAD_THRESHOLD = 2

const reload = ({ latestClientVersion, currentClientVersion }) => {
  // ONLY if the client major version is behind the latest client major version, we do a force reload
  if (window.rg4js) {
    window.rg4js('send', {
      error: new Error(`FORCE RELOAD: Current client version ${currentClientVersion} is behind latest client version ${latestClientVersion}  -> force browser reload`),
      tags: [ 'ApiVersionMismatchReload' ],
    })
  }

  // window.location.reload(true) is unreliable: the "force reload from server" boolean has been
  // ignored by modern browsers (incl. iOS WebKit) for years, so the browser can re-serve the same
  // stale index.html from its disk cache and reload straight back into the outdated bundle -> a
  // crash/reload loop, and stale clients never pick up the new asset hashes. Instead we reload the
  // document with a cache-busting query param, which the browser (and CDN) cannot satisfy from cache,
  // forcing a fresh index.html that references the current bundle.
  try {
    if (window.sessionStorage.getItem(RELOAD_GUARD_KEY) === latestClientVersion) {
      // already attempted a hard reload for this target version in this tab; avoid looping
      return
    }

    window.sessionStorage.setItem(RELOAD_GUARD_KEY, latestClientVersion)
  } catch {
    // sessionStorage unavailable (private mode / quota) -> fall through to the cache-busting reload
  }

  const url = new URL(window.location.href)

  url.searchParams.set('v', latestClientVersion)

  window.location.replace(url.toString())
}

export default ({
  $axios,
  packageVersion,
}) => {
  // Interceptor to handle major version mismatches between client and server
  $axios.interceptors.response.use((response) => {
    try {
      const xAppVersionServer = response.headers['x-app-version-server']

      if (xAppVersionServer) {
        const [
          serverVersion,
          serverBuildDate,
        ] = xAppVersionServer.split(' ')

        packageVersion.server.version = serverVersion
        packageVersion.server.buildDate = serverBuildDate
      }

      // header shape is "<version>" or "<version> <buildDate>". Only the version is compared, the
      // build date is bookkeeping. Production has sent no client build date since CLIENT_BUILD_DATE
      // was dropped from the Render config (2026-04-30), and gating the check on it silently
      // disabled the force reload for every stale tab.
      const xAppVersionLatestClient = response.headers['x-app-version-client']

      if (!xAppVersionLatestClient) {
        return response
      }

      const [
        latestClientVersion,
        latestClientBuildDate,
      ] = xAppVersionLatestClient.split(' ')
      const currentClientVersion = packageVersion.client.version

      packageVersion.latestClient.version = latestClientVersion
      packageVersion.latestClient.buildDate = latestClientBuildDate

      const [
        latestClientMajorVersion,
        latestClientFeatureVersion,
        latestClientPatchVersion,
      ] = latestClientVersion.split('.')
        .map(number => parseInt(number, 10))
      const [
        currentClientMajorVersion,
        currentClientFeatureVersion,
        currentClientPatchVersion,
      ] = currentClientVersion.split('.')
        .map(number => parseInt(number, 10))

      // reload() is invoked for its side effect only (it triggers an async cache-busting
      // navigation, or no-ops when the per-tab loop guard already fired). We must still return the
      // original response below so the interceptor resolves with the axios response and never with
      // undefined - returning undefined would break callers awaiting this request.

      // major version behind - update
      if (currentClientMajorVersion < latestClientMajorVersion) {
        reload({
          latestClientVersion,
          currentClientVersion,
        })
      } else if (currentClientMajorVersion === latestClientMajorVersion) {
        // feature version behind - update
        if (currentClientFeatureVersion < latestClientFeatureVersion) {
          reload({
            latestClientVersion,
            currentClientVersion,
          })
        } else if (currentClientFeatureVersion === latestClientFeatureVersion) {
          // patch version behind - only force a reload once the running client is more than
          // PATCH_RELOAD_THRESHOLD patches behind. Smaller gaps are left to chunk-reload.js so we
          // don't churn users while a patch is still rolling out (CDN lag, several patches per day).
          if (latestClientPatchVersion - currentClientPatchVersion > PATCH_RELOAD_THRESHOLD) {
            reload({
              latestClientVersion,
              currentClientVersion,
            })
          }
        }
      }
    } catch (err) {
      console.error(err)

      return response
    }

    return response
  })
}
